import * as React from "react";
import { usePersistedState } from "@/hooks/use-persisted-state";
import { translate, type Language } from "@/lib/i18n";

type LanguageContextValue = { language: Language; setLanguage: (next: Language) => void };

const LanguageContext = React.createContext<LanguageContextValue>({ language: "en", setLanguage: () => {} });

export function useLanguage() {
  return React.useContext(LanguageContext);
}

const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "TEXTAREA", "CODE", "PRE"]);
const ATTRIBUTES = ["placeholder", "aria-label", "title"];

function isSkipped(node: Element | null) {
  return !!node?.closest?.("[data-no-translate]");
}

function translateTree(root: Node, language: Language) {
  if (!(root instanceof Element) && !(root instanceof Document) && root.nodeType !== Node.TEXT_NODE) return;

  if (root.nodeType === Node.TEXT_NODE) {
    const parent = root.parentElement;
    if (parent && (SKIP_TAGS.has(parent.tagName) || isSkipped(parent))) return;
    const next = translate(root.nodeValue ?? "", language);
    if (next !== null) root.nodeValue = next;
    return;
  }

  const element = root as Element;
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parent = node.parentElement;
      if (!parent || SKIP_TAGS.has(parent.tagName) || isSkipped(parent)) return NodeFilter.FILTER_REJECT;
      return node.nodeValue && node.nodeValue.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
    },
  });
  const texts: Text[] = [];
  while (walker.nextNode()) texts.push(walker.currentNode as Text);
  for (const node of texts) {
    const next = translate(node.nodeValue ?? "", language);
    if (next !== null) node.nodeValue = next;
  }

  const candidates = element.matches?.("[placeholder],[aria-label],[title]") ? [element] : [];
  candidates.push(...Array.from(element.querySelectorAll("[placeholder],[aria-label],[title]")));
  for (const node of candidates) {
    if (isSkipped(node)) continue;
    for (const attribute of ATTRIBUTES) {
      const value = node.getAttribute(attribute);
      if (!value) continue;
      const next = translate(value, language);
      if (next !== null) node.setAttribute(attribute, next);
    }
  }
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguage] = usePersistedState<Language>("deuxda-language", "en");

  React.useEffect(() => {
    document.documentElement.lang = language;
    let frame = 0;
    const pending: Node[] = [];

    const observer = new MutationObserver((records) => {
      for (const record of records) {
        if (record.type === "characterData") pending.push(record.target);
        else record.addedNodes.forEach((node) => pending.push(node));
      }
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const batch = pending.splice(0, pending.length);
        observer.disconnect();
        for (const node of batch) if (node.isConnected) translateTree(node, language);
        observer.observe(document.body, { subtree: true, childList: true, characterData: true });
      });
    });

    const sweep = () => {
      observer.disconnect();
      translateTree(document.body, language);
      observer.observe(document.body, { subtree: true, childList: true, characterData: true });
    };

    sweep();
    // Catch content that mounts slightly later (charts, portals, lazy panels).
    const timers = [120, 400, 1200, 2500].map((delay) => window.setTimeout(sweep, delay));

    return () => {
      if (frame) cancelAnimationFrame(frame);
      timers.forEach((timer) => window.clearTimeout(timer));
      observer.disconnect();
    };
  }, [language]);

  const value = React.useMemo(() => ({ language, setLanguage }), [language, setLanguage]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}
